import { ChevronLeft } from 'lucide-react';

import { PortalRenderer } from '@/components/PortalRenderer';
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useConfigStore } from '@/store/ConfigStore';
import { useModalStore } from '@/store/ModalStore';

interface LegalProps {
  trigger?: React.ReactNode;
}

export const Legal = ({ trigger }: LegalProps) => {
  const portalSelector = useConfigStore((state) => state.portals?.['legal']);

  return (
    <PortalRenderer portalSelector={portalSelector}>
      <LegalInternal trigger={trigger} />
    </PortalRenderer>
  );
};

const LegalInternal = ({ trigger }: LegalProps) => {
  const { activeModal, setActiveModal } = useModalStore();
  const config = useConfigStore((state) => state.config);
  const clientName = config?.name || 'Clean Air Routes London';
  const isOpen = activeModal === 'legal';

  const handleOpenChange = (open: boolean) => {
    setActiveModal(open ? 'legal' : null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <button className="cursor-pointer text-left text-lg text-[var(--blue-one)] underline underline-offset-4 dark:text-white dark:opacity-50 hover:dark:opacity-100">
            Legal
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-hidden sm:max-h-fit sm:max-w-sm [&>.close]:hidden">
        <DialogClose className="flex w-full cursor-pointer items-center gap-2 text-left text-sm text-[var(--blue-one)]">
          <ChevronLeft strokeWidth={1} className="-ml-2 h-5" />
          <span className="underline underline-offset-4">Back to map</span>
        </DialogClose>

        <ScrollArea className="max-h-[80vh] pr-4">
          <div className="flex flex-col gap-4">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold text-[#474C5E] dark:text-white">
                Legal information
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 text-[#474C5E] dark:text-white">
              <p>
                We anonymously collect information every time you use {clientName}. This helps us understand how you use
                it. We collect the:
              </p>
              <ul className="list-disc pl-8 text-[#474C5E] dark:text-white">
                <li>mode of transport</li>
                <li>results offered</li>
                <li>route start and end points, rounded to the nearest 250 metres squared (m2)</li>
              </ul>
              <p>
                By using the {clientName} application, you acknowledge and agree to{' '}
                <Link href="https://www.westminster.gov.uk">Westminster City Council's</Link>, the Clean Air Routes
                London's developers,{' '}
                <Link href="https://www.westminster.gov.uk/terms-and-conditions">terms and conditions</Link>,{' '}
                <Link href="https://www.westminster.gov.uk/data-protection/privacy">privacy notice</Link> and{' '}
                <Link href="#">legal disclaimer</Link>.
              </p>
              <p>
                We use Google Maps' to provide the routes and directions. By using the {clientName} application you
                agree to <Link href="http://www.google.com/intl/en/policies/terms">Google's terms of service</Link> and{' '}
                <Link href="http://www.google.com/policies/privacy">privacy policy</Link>.
              </p>
            </div>

            <div className="mt-8 space-y-2">
              <img src="/wcc-logo.png" alt="Westminster City Council" className="h-12" />
              <p className="text-[#474C5E] dark:text-white">
                {clientName}, a <Link href="https://www.westminster.gov.uk/">Westminster City Council</Link> product.
              </p>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

const Link = (props: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
  <a
    className="text-[var(--blue-one)] underline underline-offset-4 dark:text-white dark:opacity-50 hover:dark:opacity-100"
    target="_blank"
    {...props}
  >
    {props.children}
  </a>
);
